// ═══════════════════════════════════════════════════════════════════════════════
// Admin Portal auth context (independent from end-user AuthContext)
// ═══════════════════════════════════════════════════════════════════════════════

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AdminUserPublic } from '@fdh/shared';
import {
  adminApi,
  getAdminStoredToken,
  setAdminStoredToken,
  removeAdminStoredToken,
  tryAdminRefreshToken,
} from '../lib/adminApi';
import { AdminIdleSessionGuard } from '../components/admin/AdminIdleSessionGuard';

type AdminAuthState = {
  admin: AdminUserPublic | null;
  isAuthenticated: boolean;
  isLoading: boolean;
};

export type AdminLoginResult =
  | { status: 'authenticated'; admin: AdminUserPublic }
  | { status: 'requires_two_factor' };

type AdminLoginOptions = {
  breakGlass?: boolean;
};

type AdminAuthContextValue = AdminAuthState & {
  login: (
    email: string,
    password: string,
    twoFactorCode?: string,
    options?: AdminLoginOptions,
  ) => Promise<AdminLoginResult>;
  resendTwoFactorCode: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAdmin: () => Promise<AdminUserPublic | null>;
};

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

export const useAdminAuth = (): AdminAuthContextValue => {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return ctx;
};

type AdminAuthProviderProps = {
  children: ReactNode;
};

const ADMIN_USER_KEY = 'adminUser';

export const AdminAuthProvider = ({ children }: AdminAuthProviderProps) => {
  const [state, setState] = useState<AdminAuthState>({
    admin: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const refreshAdmin = useCallback(async (): Promise<AdminUserPublic | null> => {
    const token = getAdminStoredToken();
    if (!token) {
      const refreshed = await tryAdminRefreshToken();
      if (!refreshed) {
        setState({ admin: null, isAuthenticated: false, isLoading: false });
        return null;
      }
    }

    try {
      const response = await adminApi.get<AdminUserPublic>('/admin/auth/me');
      if (response.success && response.data) {
        localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(response.data));
        setState({
          admin: response.data,
          isAuthenticated: true,
          isLoading: false,
        });
        return response.data;
      }
    } catch {
      // fall through
    }

    removeAdminStoredToken();
    localStorage.removeItem(ADMIN_USER_KEY);
    setState({ admin: null, isAuthenticated: false, isLoading: false });
    return null;
  }, []);

  useEffect(() => {
    const init = async () => {
      const token = getAdminStoredToken();
      if (!token) {
        const refreshed = await tryAdminRefreshToken();
        if (!refreshed) {
          setState({ admin: null, isAuthenticated: false, isLoading: false });
          return;
        }
      }

      try {
        await refreshAdmin();
      } catch {
        removeAdminStoredToken();
        localStorage.removeItem(ADMIN_USER_KEY);
        setState({ admin: null, isAuthenticated: false, isLoading: false });
      }
    };

    void init();
  }, [refreshAdmin]);

  // Proactive refresh while logged in (keeps access token alive during active work)
  useEffect(() => {
    if (!state.isAuthenticated) {
      return undefined;
    }
    if (state.admin?.breakGlassSession) {
      return undefined;
    }

    const id = window.setInterval(() => {
      void tryAdminRefreshToken();
    }, 10 * 60 * 1000);

    return () => window.clearInterval(id);
  }, [state.isAuthenticated, state.admin?.breakGlassSession]);

  const login = useCallback(
    async (
      email: string,
      password: string,
      twoFactorCode?: string,
      options?: AdminLoginOptions,
    ): Promise<AdminLoginResult> => {
      const response = await adminApi.post<{
        accessToken: string | null;
        admin: AdminUserPublic | null;
        requiresTwoFactor: boolean;
      }>('/admin/auth/login', {
        email: email.trim(),
        password,
        ...(twoFactorCode?.trim() ? { twoFactorCode: twoFactorCode.trim() } : {}),
        ...(options?.breakGlass ? { breakGlass: true } : {}),
      });

      if (!response.success || !response.data) {
        throw new Error(response.error ?? 'Login failed');
      }

      if (response.data.requiresTwoFactor) {
        return { status: 'requires_two_factor' };
      }

      if (!response.data.accessToken || !response.data.admin) {
        throw new Error(response.error ?? 'Login failed');
      }

      setAdminStoredToken(response.data.accessToken);
      localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(response.data.admin));
      setState({
        admin: response.data.admin,
        isAuthenticated: true,
        isLoading: false,
      });
      return { status: 'authenticated', admin: response.data.admin };
    },
    []
  );

  const resendTwoFactorCode = useCallback(async (email: string, password: string) => {
    const response = await adminApi.post<{ sent: true }>('/admin/auth/resend-2fa', {
      email: email.trim(),
      password,
    });
    if (!response.success) {
      throw new Error(response.error ?? 'Failed to resend code');
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await adminApi.post('/admin/auth/logout');
    } catch {
      // ignore network errors on logout
    }
    removeAdminStoredToken();
    localStorage.removeItem(ADMIN_USER_KEY);
    setState({ admin: null, isAuthenticated: false, isLoading: false });
  }, []);

  const value = useMemo<AdminAuthContextValue>(
    () => ({
      ...state,
      login,
      resendTwoFactorCode,
      logout,
      refreshAdmin,
    }),
    [state, login, resendTwoFactorCode, logout, refreshAdmin]
  );

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
      <AdminIdleSessionGuard />
    </AdminAuthContext.Provider>
  );
};
